import React, { useState, useEffect, useMemo } from 'react';
import { db, storage } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { ref as storageRef, getDownloadURL, listAll } from 'firebase/storage';
import { FaChartBar, FaSearch, FaSpinner, FaMapMarkerAlt, FaChartLine } from 'react-icons/fa';
import dayjs from 'dayjs';
import TakeoverDetail from './TakeoverDetail';

const MOBILE_MQ = '(max-width: 768px)';

const useIsMobile = () => {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => setMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return mobile;
};

const DIRECT_PHOTO_FIELDS = [
  'activationPhotos',
  'activationPhotoUrls',
  'activationPhotoURLs',
  'takeoverPhotos',
  'photoUrls',
  'photoURLs',
  'photos',
];

const EMPTY_PHOTOS = [];

const getStringFromPhotoEntry = (entry) => {
  if (!entry) return '';
  if (typeof entry === 'string') return entry;
  if (typeof entry !== 'object') return '';
  return entry.url || entry.downloadURL || entry.downloadUrl || entry.path || entry.fullPath || '';
};

const getActivationPhotoEntries = (takeover) => {
  const entries = [];
  DIRECT_PHOTO_FIELDS.forEach((field) => {
    const value = takeover?.[field];
    if (Array.isArray(value)) {
      entries.push(...value.map(getStringFromPhotoEntry).filter(Boolean));
    } else if (value && typeof value === 'object') {
      entries.push(...Object.values(value).map(getStringFromPhotoEntry).filter(Boolean));
    } else {
      const entry = getStringFromPhotoEntry(value);
      if (entry) entries.push(entry);
    }
  });
  return Array.from(new Set(entries));
};

const getActivationPhotoIds = (takeover) => (
  [
    takeover?.id,
    takeover?.takeoverId,
    takeover?.takeoverID,
    takeover?.orderId,
    takeover?.orderID,
    takeover?.takeoverDspCampaignId,
  ]
    .filter(Boolean)
    .map((id) => String(id).trim())
    .filter(Boolean)
);

const buildActivationPhotoFolderCandidates = (takeover) => {
  const ids = getActivationPhotoIds(takeover);
  return Array.from(new Set(ids.map((id) => `activationPhotos/${id}`)));
};

const resolveActivationPhotoUrl = async (entry) => {
  if (/^https?:\/\//i.test(entry)) return entry;
  const normalizedPath = entry.replace(/^gs:\/\/elite-ios-a6a68\.appspot\.com\//, '');
  return getDownloadURL(storageRef(storage, normalizedPath));
};

const loadTakeoverPhotoUrls = async (takeover, { allowLooseRootFallback = false } = {}) => {
  const directEntries = getActivationPhotoEntries(takeover);
  const directUrls = await Promise.all(directEntries.map(resolveActivationPhotoUrl));
  const folderUrls = [];
  const ids = getActivationPhotoIds(takeover);

  for (const folderPath of buildActivationPhotoFolderCandidates(takeover)) {
    try {
      const folder = await listAll(storageRef(storage, folderPath));
      if (folder.items.length > 0) {
        const urls = await Promise.all(folder.items.map((itemRef) => getDownloadURL(itemRef)));
        folderUrls.push(...urls);
        break;
      }
    } catch (err) {
      // Keep trying the next likely folder id before surfacing an error.
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`Unable to load activation photos from ${folderPath}:`, err);
      }
    }
  }

  if (directUrls.length === 0 && folderUrls.length === 0) {
    try {
      const rootFolder = await listAll(storageRef(storage, 'activationPhotos'));
      const matchingItems = rootFolder.items.filter((itemRef) => (
        ids.some((id) => itemRef.name.includes(id) || itemRef.fullPath.includes(id))
      ));
      const itemsToUse = matchingItems.length > 0
        ? matchingItems
        : allowLooseRootFallback && rootFolder.items.length <= 5 ? rootFolder.items : [];
      const urls = await Promise.all(itemsToUse.map((itemRef) => getDownloadURL(itemRef)));
      folderUrls.push(...urls);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Unable to load activation photos from activationPhotos:', err);
      }
    }
  }

  return Array.from(new Set([...directUrls, ...folderUrls]));
};

const PhotoModal = ({ photo, onClose }) => {
  if (!photo) return null;

  return (
    <div
      className="fixed inset-0 z-[13000] flex items-stretch justify-center bg-black/50 p-0 md:items-center md:p-6"
      onClick={onClose}
    >
      <div
        className="flex h-dvh w-full max-w-[960px] flex-col overflow-hidden bg-white md:h-auto md:max-h-[100dvh] md:rounded-xl md:shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <div className="text-sm font-bold">{photo.title || 'Takeover Photo'}</div>
            {photo.subtitle && <div className="text-xs text-slate-500">{photo.subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-2xl text-slate-500"
            aria-label="Close photo"
          >
            &times;
          </button>
        </div>
        <div className="flex flex-1 items-center justify-center bg-slate-900 p-4">
          <img
            src={photo.url}
            alt={photo.title || 'Takeover photo'}
            className="max-h-[calc(100dvh-88px)] max-w-full rounded object-contain"
          />
        </div>
      </div>
    </div>
  );
};

const TakeoverPhotosSection = ({ takeover, initialPhotos = EMPTY_PHOTOS, onOpenPhoto }) => {
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [photoError, setPhotoError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadPhotos = async () => {
      if (!takeover?.id) {
        setPhotos([]);
        return;
      }

      if (initialPhotos.length > 0) {
        setPhotos(initialPhotos);
        setPhotoError('');
        setLoadingPhotos(false);
        return;
      }

      setLoadingPhotos(true);
      setPhotoError('');

      try {
        const urls = await loadTakeoverPhotoUrls(takeover, { allowLooseRootFallback: true });
        if (isMounted) {
          setPhotos(urls);
        }
      } catch (err) {
        console.error('Failed to load takeover photos:', err);
        if (isMounted) {
          setPhotos([]);
          setPhotoError('Unable to load takeover photos.');
        }
      } finally {
        if (isMounted) setLoadingPhotos(false);
      }
    };

    loadPhotos();

    return () => {
      isMounted = false;
    };
  }, [takeover, initialPhotos]);

  return (
    <div className="f-card" style={{ margin: '12px 16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <h3 className="detail-section-label" style={{ margin: 0 }}>Takeover Photos</h3>
        {photos.length > 0 && (
          <span style={{ fontSize: 12, color: 'var(--ms-text-secondary)', fontWeight: 600 }}>
            {photos.length} photo{photos.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {loadingPhotos ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ms-text-secondary)' }}>
          <FaSpinner className="fa-spin" /> Loading photos...
        </div>
      ) : photoError ? (
        <div style={{ fontSize: 13, color: '#b91c1c' }}>{photoError}</div>
      ) : photos.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>
          No activation photos found for this takeover.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
          {photos.map((url, index) => (
            <button
              key={url}
              type="button"
              onClick={() => onOpenPhoto?.({
                url,
                title: `Takeover Photo ${index + 1}`,
                subtitle: `${takeover?.signName || 'Takeover'}${takeover?.userName ? ` • ${takeover.userName}` : ''}`,
              })}
              style={{
                display: 'block',
                height: 120,
                padding: 0,
                borderRadius: 6,
                overflow: 'hidden',
                border: '1px solid var(--ms-border-light)',
                background: '#f3f2f1',
                cursor: 'pointer',
              }}
              title={`Open takeover photo ${index + 1}`}
            >
              <img
                src={url}
                alt={`Takeover activation ${index + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Takeovers = () => {
  const [takeovers, setTakeovers] = useState([]);
  const [selectedTakeover, setSelectedTakeover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [datePreset, setDatePreset] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [groupBy, setGroupBy] = useState('none'); // none | month | day
  const [takeoverPhotosById, setTakeoverPhotosById] = useState({});
  const [photoTakeoversLoading, setPhotoTakeoversLoading] = useState(false);
  const [activePhoto, setActivePhoto] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const isMobile = useIsMobile();

  useEffect(() => { fetchTakeovers(); }, []);

  useEffect(() => {
    let isMounted = true;

    const loadPhotoTakeovers = async () => {
      if (activeTab !== 'photos') {
        setPhotoTakeoversLoading(false);
        return;
      }

      if (takeovers.length === 0) {
        setTakeoverPhotosById({});
        setPhotoTakeoversLoading(false);
        return;
      }

      setPhotoTakeoversLoading(true);
      const entries = await Promise.all(takeovers.map(async (takeover) => {
        try {
          const urls = await loadTakeoverPhotoUrls(takeover);
          return [takeover.id, urls];
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn(`Unable to index photos for takeover ${takeover.id}:`, err);
          }
          return [takeover.id, []];
        }
      }));

      if (isMounted) {
        setTakeoverPhotosById(Object.fromEntries(entries));
        setPhotoTakeoversLoading(false);
      }
    };

    loadPhotoTakeovers();

    return () => {
      isMounted = false;
    };
  }, [takeovers, activeTab]);

  const parseToDate = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    // Firestore Timestamp
    if (typeof value?.toDate === 'function') {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    }
    // Firestore { seconds, nanoseconds }
    if (typeof value === 'object' && typeof value?.seconds === 'number') {
      const d = new Date(value.seconds * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    // ISO string / millis / etc.
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const getTakeoverDate = (t) => {
    // Prefer the scheduled takeover date, then fall back to created/payment timestamps.
    const candidates = [
      t?.date,
      t?.startDate,
      t?.takeoverDate,
      t?.scheduledDate,
      t?.bookingDate,
      t?.createdAt,
      t?.paymentTimestamp,
      t?.updatedAt,
    ];
    for (const c of candidates) {
      const d = parseToDate(c);
      if (d) return d;
    }
    return null;
  };

  const fetchTakeovers = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'orders'));
      const list = await Promise.all(snap.docs.map(async (d) => {
        const data = d.data();
        let uName = 'Unknown', uEmail = '';
        if (data.userUid) {
          const uDoc = await getDoc(doc(db, 'users', data.userUid));
          if (uDoc.exists()) {
            uName = uDoc.data().name || uDoc.data().email || 'Unknown';
            uEmail = uDoc.data().email || '';
          }
        }
        return {
          id: d.id,
          ...data,
          userName: uName,
          userEmail: uEmail,
          signName: data.signName || 'Unnamed Sign',
          status: data.status || 'New',
          date: data.date || null,
          totalPrice: data.totalPrice || 0,
        };
      }));
      setTakeovers(list);
    } finally {
      setLoading(false);
    }
  };

  const topBookedSigns = useMemo(() => {
    const counts = {};
    takeovers.forEach(t => {
      const name = t.signName || 'Unknown';
      if (!counts[name]) counts[name] = { name, count: 0, revenue: 0, address: t.address, img: t.signImgURL };
      counts[name].count++;
      counts[name].revenue += Number(t.totalPrice) || 0;
    });
    return Object.values(counts).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [takeovers]);

  const takeoversWithPhotos = useMemo(() => (
    (takeovers || [])
      .map(t => {
        const d = getTakeoverDate(t);
        return {
          ...t,
          _takeoverDate: d,
          _takeoverDateMs: d ? d.getTime() : 0,
          _photos: takeoverPhotosById[t.id] || EMPTY_PHOTOS,
        };
      })
      .filter(t => t._photos.length > 0)
      .sort((a, b) => b._takeoverDateMs - a._takeoverDateMs)
  ), [takeovers, takeoverPhotosById]);

  const filtered = useMemo(() => {
    const now = dayjs();
    const fromDay = dateFrom ? dayjs(dateFrom).startOf('day') : null;
    const toDay = dateTo ? dayjs(dateTo).endOf('day') : null;

    const normalized = (takeovers || []).map(t => {
      const d = getTakeoverDate(t);
      return {
        ...t,
        _takeoverDate: d,
        _takeoverDateMs: d ? d.getTime() : 0,
      };
    });

    const matchesSearch = (t) => {
      if (!searchTerm) return true;
      const q = searchTerm.trim().toLowerCase();
      if (!q) return true;
      const hay = [
        t?.id,
        t?.signName,
        t?.userName,
        t?.userEmail,
        t?.address,
        t?.dma,
        t?.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    };

    const matchesStatus = (t) => {
      if (statusFilter === 'All') return true;
      return t.status === statusFilter;
    };

    const matchesDate = (t) => {
      const d = t._takeoverDate ? dayjs(t._takeoverDate) : null;
      if (!d) return datePreset === 'All'; // if we can't date it, only include when not filtering by date

      if (datePreset === 'All') return true;
      const ms = d.valueOf();
      const todayStartMs = now.startOf('day').valueOf();
      if (datePreset === 'Upcoming') return ms >= todayStartMs;
      if (datePreset === 'Past') return ms < todayStartMs;
      if (datePreset === 'Next7') return ms >= todayStartMs && ms < now.add(7, 'day').endOf('day').valueOf();
      if (datePreset === 'Next30') return ms >= todayStartMs && ms < now.add(30, 'day').endOf('day').valueOf();
      if (datePreset === 'ThisMonth') return d.isSame(now, 'month');
      if (datePreset === 'Last30') return ms > now.subtract(30, 'day').startOf('day').valueOf() && ms <= now.endOf('day').valueOf();
      if (datePreset === 'Custom') {
        if (fromDay && d.isBefore(fromDay)) return false;
        if (toDay && d.isAfter(toDay)) return false;
        return true;
      }
      return true;
    };

    const compare = (a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const safeStr = (v) => (v == null ? '' : String(v)).toLowerCase();
      if (sortKey === 'date') {
        return (a._takeoverDateMs - b._takeoverDateMs) * dir;
      }
      if (sortKey === 'amount') {
        return ((Number(a.totalPrice) || 0) - (Number(b.totalPrice) || 0)) * dir;
      }
      if (sortKey === 'status') {
        return safeStr(a.status).localeCompare(safeStr(b.status)) * dir;
      }
      if (sortKey === 'customer') {
        return safeStr(a.userName).localeCompare(safeStr(b.userName)) * dir;
      }
      if (sortKey === 'sign') {
        return safeStr(a.signName).localeCompare(safeStr(b.signName)) * dir;
      }
      return (a._takeoverDateMs - b._takeoverDateMs) * dir;
    };

    return normalized
      .filter(t => matchesSearch(t) && matchesStatus(t) && matchesDate(t))
      .sort(compare);
  }, [takeovers, searchTerm, statusFilter, datePreset, dateFrom, dateTo, sortKey, sortDir]);

  const groupedRows = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'all', label: null, rows: filtered }];
    const fmt = groupBy === 'day' ? 'YYYY-MM-DD' : 'YYYY-MM';
    const labelFmt = groupBy === 'day' ? 'dddd, MMM D, YYYY' : 'MMMM YYYY';
    const groups = new Map();
    for (const t of filtered) {
      const d = t._takeoverDate ? dayjs(t._takeoverDate) : null;
      const k = d ? d.format(fmt) : 'unknown';
      if (!groups.has(k)) {
        groups.set(k, { key: k, label: d ? d.format(labelFmt) : 'Unknown date', rows: [] });
      }
      groups.get(k).rows.push(t);
    }
    // Preserve sort order by iterating in insertion order (filtered is already sorted)
    return Array.from(groups.values());
  }, [filtered, groupBy]);

  const getStatusColor = (s) => {
    const c = { 
      'Approved': { bg: '#dcfce7', text: '#166534' }, 
      'Complete': { bg: '#dcfce7', text: '#166534' },
      'Pending': { bg: '#fffbeb', text: '#92400e' }, 
      'Upcoming': { bg: '#e0f2fe', text: '#0369a1' },
      'Rejected': { bg: '#fee2e2', text: '#991b1b' },
      'Canceled': { bg: '#fee2e2', text: '#991b1b' }
    };
    return c[s] || { bg: '#f3f2f1', text: '#64748b' };
  };

  return (
    <>
      <div className="f-page-enter mx-auto max-w-[1920px] px-4 pb-8 md:px-6">
        <div className="f-header rounded-xl border border-slate-200/80 shadow-sm">
          <div>
            <h1 className="f-title">Takeover Management</h1>
            <p className="f-subtitle mb-0">Overview of all billboard takeovers</p>
          </div>
          <div className={`flex gap-2 ${isMobile ? 'w-full flex-col' : 'flex-wrap items-center'}`}>
            <button
              type="button"
              className={`f-button ${showAnalytics ? '' : 'f-button-secondary'} ${isMobile ? 'w-full' : ''}`}
              onClick={() => setShowAnalytics(!showAnalytics)}
            >
              <FaChartBar /> {isMobile ? 'Stats' : 'Analytics'}
            </button>
            <div className={`relative ${isMobile ? 'w-full' : 'w-[240px]'}`}>
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-xs text-slate-500" />
              <input
                type="search"
                placeholder={isMobile ? 'Search takeovers...' : 'Search customer, sign, email...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="f-input !w-full !pl-9"
              />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`f-input ${isMobile ? 'w-full' : 'w-[150px]'}`}>
              <option value="All">All Statuses</option>
              {['New', 'Pending', 'Approved', 'Upcoming', 'Complete', 'Canceled', 'Rejected'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={datePreset} onChange={(e) => setDatePreset(e.target.value)} className={`f-input ${isMobile ? 'w-full' : 'w-[150px]'}`}>
              <option value="All">All dates</option>
              <option value="Upcoming">Upcoming</option>
              <option value="Past">Past</option>
              <option value="Next7">Next 7 days</option>
              <option value="Next30">Next 30 days</option>
              <option value="ThisMonth">This month</option>
              <option value="Last30">Last 30 days</option>
              <option value="Custom">Custom range</option>
            </select>
            {datePreset === 'Custom' && (
              <>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={`f-input ${isMobile ? 'w-full' : 'w-[140px]'}`} aria-label="From date" />
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={`f-input ${isMobile ? 'w-full' : 'w-[140px]'}`} aria-label="To date" />
              </>
            )}
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} className={`f-input ${isMobile ? 'w-full' : 'w-[150px]'}`}>
              <option value="date">Sort: Date</option>
              <option value="amount">Sort: Amount</option>
              <option value="status">Sort: Status</option>
              <option value="customer">Sort: Customer</option>
              <option value="sign">Sort: Sign</option>
            </select>
            <button
              type="button"
              className={`f-button f-button-secondary ${isMobile ? 'w-full' : ''}`}
              onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
              title={`Sort ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
            >
              {sortDir === 'asc' ? 'Asc' : 'Desc'}
            </button>
            {!isMobile && (
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="f-input w-[150px]">
                <option value="none">Group: None</option>
                <option value="month">Group: Month</option>
                <option value="day">Group: Day</option>
              </select>
            )}
          </div>
        </div>

        <div className="mb-6">
          <div className="mb-5 flex gap-2 overflow-x-auto border-b border-slate-200">
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`shrink-0 px-3.5 py-2.5 text-sm font-bold ${activeTab === 'all' ? 'border-b-2 border-primary text-primary' : 'border-b-2 border-transparent text-slate-500'}`}
            >
              All Takeovers
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('photos')}
              className={`shrink-0 px-3.5 py-2.5 text-sm font-bold ${activeTab === 'photos' ? 'border-b-2 border-primary text-primary' : 'border-b-2 border-transparent text-slate-500'}`}
            >
              With Photos
            </button>
          </div>

          {showAnalytics && (
            <div style={{ marginBottom: 32, borderBottom: '1px solid var(--ms-border-light)', paddingBottom: 32 }}>
              <div className={isMobile ? 'mb-6 grid grid-cols-2 gap-2' : 'f-grid'} style={isMobile ? undefined : { marginBottom: 32 }}>
                <div className={`f-card f-stat-card ${isMobile ? '!mb-0 !p-3' : ''}`}>
                  <span className="f-stat-label">{isMobile ? 'Total' : 'Total Takeovers'}</span>
                  <span className={`f-stat-value ${isMobile ? '!my-0 !text-xl' : ''}`}>{takeovers.length}</span>
                </div>
                <div className={`f-card f-stat-card ${isMobile ? '!mb-0 !p-3' : ''}`}>
                  <span className="f-stat-label">Revenue</span>
                  <span className={`f-stat-value ${isMobile ? '!my-0 !text-xl' : ''}`} style={{ color: '#107c10' }}>${takeovers.reduce((s,t) => s + (Number(t.totalPrice)||0), 0).toLocaleString()}</span>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <FaChartLine color="var(--ms-primary)" />
                <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Top Performing Signs</h3>
              </div>
              
              <div className="f-grid">
                {topBookedSigns.map((sign, idx) => (
                  <div key={idx} className="f-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ height: 100, background: '#f3f2f1', position: 'relative' }}>
                      {sign.img ? (
                        <img src={sign.img} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaMapMarkerAlt color="#64748b" size={24}/></div>
                      )}
                      <div style={{ position: 'absolute', top: 8, left: 8, background: 'var(--ms-primary)', color: '#fff', width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>{idx + 1}</div>
                    </div>
                    <div style={{ padding: 16 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sign.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 12, height: 16, overflow: 'hidden' }}>{sign.address || '—'}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid var(--ms-border-light)' }}>
                        <div>
                          <div style={{ fontSize: 10, color: 'var(--ms-text-secondary)', fontWeight: 600 }}>BOOKINGS</div>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{sign.count}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 10, color: 'var(--ms-text-secondary)', fontWeight: 600 }}>REVENUE</div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: '#107c10' }}>${sign.revenue.toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'photos' && (
          <div className="f-card" style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Takeovers With Photos</h3>
                <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)', marginTop: 4 }}>
                  Only takeovers with activation photos found in Storage are shown here.
                </div>
              </div>
              <span style={{ fontSize: 12, color: 'var(--ms-text-secondary)', fontWeight: 700 }}>
                {photoTakeoversLoading ? 'Checking...' : `${takeoversWithPhotos.length} found`}
              </span>
            </div>

            {photoTakeoversLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 16, color: 'var(--ms-text-secondary)', fontSize: 13 }}>
                <FaSpinner className="fa-spin" /> Checking Storage for takeover photos...
              </div>
            ) : takeoversWithPhotos.length === 0 ? (
              <div style={{ padding: 16, color: 'var(--ms-text-secondary)', fontSize: 13, background: '#f8fafc', border: '1px solid var(--ms-border-light)', borderRadius: 6 }}>
                No takeovers with photos found yet.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14 }}>
                {takeoversWithPhotos.map((t) => {
                  const firstPhoto = t._photos[0];
                  return (
                    <div
                      key={t.id}
                      onClick={() => setSelectedTakeover(t)}
                      style={{
                        border: '1px solid var(--ms-border-light)',
                        borderRadius: 8,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        background: '#fff',
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActivePhoto({
                            url: firstPhoto,
                            title: t.signName || 'Takeover Photo',
                            subtitle: `${t.userName || 'Unknown customer'} • ${t._photos.length} photo${t._photos.length === 1 ? '' : 's'}`,
                          });
                        }}
                        style={{
                          width: '100%',
                          height: 150,
                          border: 'none',
                          padding: 0,
                          display: 'block',
                          background: '#f3f2f1',
                          cursor: 'zoom-in',
                        }}
                        title="Open photo"
                      >
                        <img src={firstPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </button>
                      <div style={{ padding: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {t.signName}
                          </div>
                          <span style={{ flex: '0 0 auto', fontSize: 11, fontWeight: 700, color: '#0369a1', background: '#e0f2fe', padding: '2px 7px', borderRadius: 999 }}>
                            {t._photos.length}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t.userName} {t.userEmail ? `• ${t.userEmail}` : ''}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 10, fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                          <span>{t._takeoverDate ? dayjs(t._takeoverDate).format('MMM D, YYYY') : 'No date'}</span>
                          <span style={{ fontWeight: 700, color: getStatusColor(t.status).text }}>{t.status}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {activeTab === 'all' && (
          isMobile ? (
            <div className="space-y-3">
              {loading ? (
                <div className="f-card !mb-0 py-10 text-center text-sm text-slate-500">
                  <FaSpinner className="fa-spin" /> Loading...
                </div>
              ) : filtered.length === 0 ? (
                <div className="f-card !mb-0 py-10 text-center text-sm text-slate-500">
                  No takeovers match your filters.
                </div>
              ) : filtered.map((t) => {
                const colors = getStatusColor(t.status);
                const selected = selectedTakeover?.id === t.id;
                return (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => setSelectedTakeover(t)}
                    className="f-card !mb-0 w-full touch-manipulation p-4 text-left"
                    style={{
                      borderColor: selected ? '#2563eb' : undefined,
                      boxShadow: selected ? '0 0 0 2px rgba(37,99,235,0.12)' : undefined,
                      background: selected ? '#f8fafc' : undefined,
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="break-words text-[15px] font-semibold leading-snug text-slate-900">{t.signName}</div>
                        <div className="mt-1 text-xs text-slate-500">{t.userName}{t.userEmail ? ` · ${t.userEmail}` : ''}</div>
                      </div>
                      <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.text, flexShrink: 0 }}>
                        {t.status}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                      <span>{t._takeoverDate ? dayjs(t._takeoverDate).format('MMM D, YYYY') : 'No date'}</span>
                      <span className="font-semibold text-slate-800">${Number(t.totalPrice).toFixed(2)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
          <div className="f-card overflow-x-auto p-0">
            <table className="f-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Sign Location</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" style={{ textAlign: 'center', padding: 40 }}><FaSpinner className="fa-spin" /> Loading...</td></tr>
                ) : groupedRows.flatMap((g) => ([
                  ...(g.label ? [
                    <tr key={`g-${g.key}`} style={{ background: 'var(--ms-bg)', borderTop: '1px solid var(--ms-border-light)' }}>
                      <td colSpan="5" style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                        {g.label} <span style={{ fontWeight: 600, marginLeft: 8, letterSpacing: 0, textTransform: 'none' }}>({g.rows.length})</span>
                      </td>
                    </tr>
                  ] : []),
                  ...g.rows.map((t) => (
                    <tr key={t.id} onClick={() => setSelectedTakeover(t)} style={{ cursor: 'pointer' }}>
                      <td><div style={{ fontWeight: 600 }}>{t.userName}</div><div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>{t.userEmail}</div></td>
                      <td><div>{t.signName}</div></td>
                      <td>{t._takeoverDate ? dayjs(t._takeoverDate).format('MMM D, YYYY') : '—'}</td>
                      <td style={{ fontWeight: 600 }}>${Number(t.totalPrice).toFixed(2)}</td>
                      <td>
                        <span style={{ padding: '2px 8px', borderRadius: 2, fontSize: 11, fontWeight: 600, background: getStatusColor(t.status).bg, color: getStatusColor(t.status).text }}>{t.status}</span>
                      </td>
                    </tr>
                  ))
                ]))}
              </tbody>
            </table>
            {!loading && filtered.length === 0 && (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--ms-text-secondary)' }}>
                No takeovers match your filters.
              </div>
            )}
          </div>
          )
          )}
        </div>
      </div>

      {/* Side Panel for Detail */}
      {selectedTakeover && (
        <div className="f-panel-overlay" style={{ zIndex: 11000 }} onClick={() => setSelectedTakeover(null)}>
          <div className="f-panel" onClick={e => e.stopPropagation()}>
            <div className="f-panel-header">
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Takeover Details</h2>
              <button
                type="button"
                onClick={() => setSelectedTakeover(null)}
                className="flex h-11 w-11 items-center justify-center rounded-lg text-2xl text-slate-500"
                aria-label="Close takeover details"
              >
                &times;
              </button>
            </div>
            <TakeoverPhotosSection
              takeover={selectedTakeover}
              initialPhotos={takeoverPhotosById[selectedTakeover.id] || EMPTY_PHOTOS}
              onOpenPhoto={setActivePhoto}
            />
            <TakeoverDetail takeover={selectedTakeover} refreshTakeover={fetchTakeovers} onClose={() => setSelectedTakeover(null)} />
          </div>
        </div>
      )}
      <PhotoModal photo={activePhoto} onClose={() => setActivePhoto(null)} />
    </>
  );
};

export default Takeovers;
